import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Select, Typography } from 'antd';

import useSwr from '@hooks/useSwr';
import ActionTypes from '@utils/deviceActions';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SelectCustomers from '@components/SelectCustomers';
import { IContract, IDeviceActionRequestPayload } from '../types';
import useActivationUploadStep from './Activation/ActivationUploadStep';

const { Paragraph } = Typography;

interface IPriorities {
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  actionKey: number;
  uploadedDevicesState: any;
  currentStep: number;
  manualUploadDevices: any;
}

const useActivationMot = ({
  actionKey,
  handleDeviceActions,
  uploadedDevicesState,
  currentStep,
  manualUploadDevices,
}: IPriorities) => {
  const [selectedContract, setSelectedContract] = useState<any>();
  const [uploadedDevices, setUploadedDevices] = uploadedDevicesState;
  const [selectedCustomer, setSelectedCustomer] = useState<
    number | string | undefined
  >();
  const [fileOrManualUpload, setfileOrManualUpload] = useState<number>(0); // 0 file , 1 manual
  const [stringManualUpload, setStringManualUpload] = useState('');
  const [manualUpload, setManualUpload] = manualUploadDevices;

  const { keycloak } = useContext(KeycloakContext);

  const loggedRoot = useMemo(() => {
    return keycloak.idTokenParsed?.customerType === 'ROOT';
  }, [keycloak]);

  const { t: translate } = useTranslation([
    'device-actions-modal',
    'device-actions',
  ]);

  const contractsMustFetch = useMemo(() => {
    if (actionKey === ActionTypes.ACTIVATION_MOT) {
      if (!loggedRoot) {
        return {
          params: {
            customerId: keycloak?.idTokenParsed?.customerId,
            product: 'MOT',
          },
        };
      }
      if (loggedRoot && selectedCustomer) {
        return {
          params: {
            customerId: selectedCustomer,
            product: 'MOT',
          },
        };
      }
    }

    return undefined;
  }, [actionKey, keycloak.idTokenParsed, loggedRoot, selectedCustomer]);

  const { data: dataContracts, error: errorContracts } = useSwr<IContract[]>(
    contractsMustFetch ? '/service-proxy/contracts' : null,
    contractsMustFetch,
  );

  const contractOptions = useMemo(
    () =>
      Array.isArray(dataContracts)
        ? dataContracts
            .filter(item => item.flagIsca !== '1')
            .map(({ planName, assetNumber, nickname }) => ({
              value: assetNumber,
              label: `${assetNumber} ${
                nickname ? ` - ${nickname}` : ''
              } - ${planName}`,
            }))
        : [],
    [dataContracts],
  );

  const { step: activationUploadStep, uploadReinitState } =
    useActivationUploadStep({
      selectedAcronym: 'MOT',
      translate,
      fileOrManualUploadState: [fileOrManualUpload, setfileOrManualUpload],
      stringManualUploadState: [stringManualUpload, setStringManualUpload],
      actionKey,
      setUploadedDevices,
      setManualUpload,
    });

  const contentSteps = useMemo(() => {
    // ROOT ONLY STEP
    const clientSelectionStep = {
      title: translate('steps.client.name'),
      content: (
        <div className="action-activation">
          <Paragraph>{`${translate('steps.client.description')}:`}</Paragraph>
          <Row align="middle" justify="start">
            <SelectCustomers
              value={selectedCustomer && Number(selectedCustomer)}
              onChange={value => setSelectedCustomer(value)}
            />
          </Row>
        </div>
      ),
    };

    // OTHERS THAN ROOT
    const everyoneStep = {
      title: translate('steps.contract.name'),
      content: (
        <div className="action-activation">
          <Paragraph>{`${translate('steps.contract.description')}:`}</Paragraph>
          <Row align="middle" justify="start">
            {errorContracts ? (
              <p>{translate('not-found-contract')}</p>
            ) : (
              <Select
                loading={!contractOptions}
                optionFilterProp="label"
                options={contractOptions}
                value={selectedContract}
                allowClear
                showSearch
                onChange={value => {
                  setSelectedContract(value);
                }}
              />
            )}
          </Row>
        </div>
      ),
    };

    if (loggedRoot) {
      return [clientSelectionStep, everyoneStep, activationUploadStep];
    }
    return [everyoneStep, activationUploadStep];
  }, [
    contractOptions,
    loggedRoot,
    selectedContract,
    selectedCustomer,
    translate,
    errorContracts,
    activationUploadStep,
  ]);

  const reinitState = () => {
    uploadReinitState();
    setSelectedContract(undefined);
    setSelectedCustomer(undefined);
    setfileOrManualUpload(0);
    setStringManualUpload('');
    setManualUpload([]);
  };

  const nextStepIsDisabled = useCallback(() => {
    if (loggedRoot) {
      if (currentStep === 0) {
        return !selectedCustomer;
      }
      if (currentStep === 1) {
        return !selectedContract;
      }

      if (currentStep === 3) {
        if (fileOrManualUpload === 0) {
          if (
            !uploadedDevices ||
            (uploadedDevices && uploadedDevices?.length === 0)
          )
            return true;
        } else if (stringManualUpload.length === 0) return true;
      }

      return false;
    }
    if (currentStep === 0) {
      return !selectedContract;
    }

    if (currentStep === 1) {
      if (fileOrManualUpload === 0) {
        if (
          !uploadedDevices ||
          (uploadedDevices && uploadedDevices?.length === 0)
        )
          return true;
      } else if (stringManualUpload.length === 0) return true;
    }

    return false;
  }, [
    uploadedDevices,
    selectedCustomer,
    selectedContract,
    currentStep,
    loggedRoot,
    stringManualUpload.length,
    fileOrManualUpload,
  ]);

  const onSubmit = useCallback(async () => {
    const devicesToSend = uploadedDevices;
    const payload: IDeviceActionRequestPayload = {};
    payload.action = actionKey;
    payload.customer = selectedCustomer; // is a id
    payload.contractId = selectedContract;

    if (fileOrManualUpload === 0) {
      payload.devices = devicesToSend || [];
    } else if (fileOrManualUpload === 1) {
      payload.devices = manualUpload;
    }
    return handleDeviceActions(payload);
  }, [
    selectedContract,
    selectedCustomer,
    uploadedDevices,
    handleDeviceActions,
    actionKey,
    manualUpload,
    fileOrManualUpload,
  ]);

  const activationMotData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { activationMotData };
};

export default useActivationMot;
